import { debounce } from "@mui/material/utils";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";

import Box from "@mui/material/Box";
import Skeleton from "@mui/material/Skeleton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { useTranslation } from "react-i18next";
import {
	useGetCompanyWithIdContactPersonsQuery,
	useUpdateCompanyWithIdContactPersonsMutation,
} from "../../../../features/companies/companiesApiSlice";

const ContactPerson = ({ companyId }) => {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [updateContactPerson, { isLoading: isUpdating }] =
		useUpdateCompanyWithIdContactPersonsMutation();
	const { data, error, isLoading } =
		useGetCompanyWithIdContactPersonsQuery(companyId);

	const [value, setValue] = useState("");

	useEffect(() => {
		if (data) {
			setValue(data);
		}
	}, [data, setValue]);

	const onChange = (e) => {
		e.preventDefault();
		const newValue = e.target.value;
		setValue(newValue);
		debouncedSave(newValue);
	};

	const debouncedSave = useCallback(
		debounce(async (newValue) => {
			await updateContactPerson({
				companyId,
				info: newValue,
			})
				.unwrap()
				.then((_payload) => {
					enqueueSnackbar(t("updated_data"), {
						variant: "success",
					});
				})
				.catch((error) => {
					if (error?.data?.errors) {
						const errors = error.data.errors;
						Object.entries(errors).forEach(([key, message]) => {
							enqueueSnackbar(message[0], {
								variant: "error",
								preventDuplicate: true,
							});
						});
					} else {
						enqueueSnackbar(error?.data?.message || t("default_error"), {
							variant: "error",
							preventDuplicate: true,
						});
					}
				});
		}, 1000),
		[]
	);

	if (isLoading) {
		return <Skeleton variant="rectangular" height={77} />;
	}

	return (
		<Box
			sx={{
				display: "flex",
				gap: "10px",
				alignItems: "start",
				width: "100%",
			}}
		>
			<Box
				sx={{
					flex: "0",
					display: "flex",
					alignItems: "center",
					height: "100%",
				}}
			>
				<Typography
					variant="label"
					component="label"
					htmlFor="bootstrap-input"
					color={error && "error"}
					sx={{
						whiteSpace: "nowrap",
						margin: "none",
					}}
				>
					{t("contact_person_short")}:
				</Typography>
			</Box>
			<Box
				sx={{
					flex: "1",
					display: "flex",
					alignItems: "center",
				}}
			>
				<TextField
					id="bootstrap-input"
					size="small"
					value={value}
					variant="outlined"
					fullWidth
					onChange={onChange}
					multiline
					minRows={3}
					maxRows={3}
					defaultValue=""
					slotProps={{
						inputLabel: { shrink: true },
					}}
				/>
			</Box>
		</Box>
	);
};

export default ContactPerson;
